<script setup>
import { computed, useTemplateRef } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import CardAvatar from './CardAvatar.vue';
import CardContent from './CardContent.vue';
import MessagePreview from './MessagePreview.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';
import InboxName from 'dashboard/components-next/Conversation/InboxName.vue';
import Avatar from 'next/avatar/Avatar.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import SLACardLabel from 'dashboard/components-next/Conversation/Sla/SLACardLabel.vue';
import CardWorkflowStageIcon from './CardWorkflowStageIcon.vue';
import UnreadBadge from './UnreadBadge.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { getWorkflowStageLabel } from 'dashboard/helper/workflowStageHelper';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  isInboxView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'selectConversation',
  'deSelectConversation',
  'click',
  'contextmenu',
]);

const lastMessageInChat = computed(() => getLastMessage(props.chat));
const firstMessageInChat = computed(() => props.chat.first_message);

const accountLabels = useMapGetter('labels/getLabels');
const primaryLabel = computed(() => {
  const title = props.chat.labels?.[0];
  if (!title) return null;
  return accountLabels.value.find(label => label.title === title) || null;
});

const stageLabel = computed(() =>
  getWorkflowStageLabel(props.chat.workflow_stage, props.chat.workflow_type)
);

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const unreadCount = computed(() => props.chat.unread_count);

const slaCardLabel = useTemplateRef('slaCardLabel');

const hasSlaPolicyId = computed(
  () =>
    !props.currentContact?.blocked &&
    (props.chat?.applied_sla?.id || slaCardLabel.value?.hasSlaThreshold)
);

const selectedModel = computed({
  get: () => props.selected,
  set: value => {
    if (value) {
      emit('selectConversation', value);
    } else {
      emit('deSelectConversation', value);
    }
  },
});
</script>

<template>
  <div
    class="conversation relative cursor-pointer group grid grid-cols-[minmax(0,20rem)_minmax(0,1fr)_11rem_9rem_9rem_4.5rem] gap-4 items-center px-3 py-2.5 border-b border-n-slate-3 hover:border-n-surface-1 hover:z-[1] before:content-[none] before:absolute before:-top-px before:inset-x-0 before:h-px before:bg-n-surface-1 before:pointer-events-none hover:before:content-['']"
    :class="{
      'active animate-card-select bg-n-alpha-1 dark:bg-n-alpha-3 !border-n-surface-1':
        isActiveChat,
      'selected bg-n-slate-2 dark:bg-n-slate-3 !border-n-surface-1': selected,
      'hover:bg-n-alpha-1': !isActiveChat && !selected,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <!-- COLUMN 1: Conversation -->
    <div class="flex items-center gap-2 min-w-0">
      <div class="flex items-center justify-center flex-shrink-0" @click.stop>
        <Checkbox v-model="selectedModel" />
      </div>

      <div class="w-4 flex items-center justify-center flex-shrink-0">
        <CardPriorityIcon :priority="chat.priority" show-empty />
      </div>

      <div class="w-4 flex items-center justify-center flex-shrink-0">
        <Avatar
          v-if="showAssignee && assignee.name"
          v-tooltip.top="{
            content: assignee.name,
            delay: { show: 500, hide: 0 },
          }"
          :name="assignee.name"
          :src="assignee.thumbnail"
          :size="14"
          :status="assignee.availability_status"
          hide-offline-status
        />
        <Icon
          v-else
          icon="i-woot-empty-assignee"
          class="size-4 text-n-slate-7"
        />
      </div>

      <div class="w-5 flex items-center justify-center flex-shrink-0">
        <CardWorkflowStageIcon
          :workflow-stage="chat.workflow_stage"
          :workflow-type="chat.workflow_type"
        />
      </div>

      <div v-if="!isInboxView && showInboxName" class="w-16 flex-shrink-0">
        <InboxName :inbox="inbox" class="min-w-0" />
      </div>

      <CardAvatar
        v-tooltip.top="{
          content: `#${chat.id}`,
          delay: { show: 500, hide: 0 },
        }"
        :contact="currentContact"
        :selected="false"
        :enable-selection="false"
        :hide-thumbnail="false"
        :size="36"
      />

      <div class="min-w-0 flex-1">
        <h4
          class="text-heading-3 my-0 capitalize truncate text-n-slate-12 font-medium"
        >
          {{ currentContact.name }}
        </h4>
        <div
          v-if="firstMessageInChat"
          class="flex items-baseline gap-1 min-w-0 text-xs text-n-slate-10"
        >
          <span class="flex-shrink-0">First:</span>
          <MessagePreview
            :message="firstMessageInChat"
            :show-message-type="false"
            class="min-w-0 flex-1 truncate"
          />
        </div>
      </div>
    </div>

    <!-- COLUMN 2: Latest message -->
    <div class="min-w-0">
      <CardContent
        :last-message="lastMessageInChat"
        :voice-call-status="voiceCallData.status"
        :voice-call-direction="voiceCallData.direction"
        :unread-count="unreadCount"
        :show-expanded-preview="false"
      />
    </div>

    <!-- COLUMN 3: Stage -->
    <div class="min-w-0">
      <span
        :title="stageLabel"
        class="inline-block max-w-full px-2 py-1 rounded-md bg-n-slate-3 text-n-slate-11 text-xs font-medium truncate"
      >
        {{ stageLabel }}
      </span>
    </div>

    <!-- COLUMN 4: Topic -->
    <div class="min-w-0">
      <span
        class="inline-block max-w-full px-2 py-1 rounded-md bg-n-slate-3 text-n-slate-11 text-xs truncate"
      >
        {{ chat.topic_name || '—' }}
      </span>
    </div>

    <!-- COLUMN 5: Updated -->
    <div class="flex items-center justify-end gap-2 min-w-0">
      <Label
        v-if="primaryLabel"
        :label="primaryLabel"
        compact
        class="flex-shrink-0"
      />
      <SLACardLabel v-if="hasSlaPolicyId" ref="slaCardLabel" :chat="chat" />
      <TimeAgo
        :conversation-id="chat.id"
        :last-activity-timestamp="chat.timestamp"
        :created-at-timestamp="chat.created_at"
        class="font-440 !text-xs text-n-slate-11 flex-shrink-0"
      />
    </div>

    <!-- COLUMN 6: Unread -->
    <div class="flex justify-end">
      <UnreadBadge :count="unreadCount" />
    </div>
  </div>
</template>
